// Cross-feature formatter/helper functions
import { priceToString } from 'shared/utils';
import { AUTH_METHODS, INTERNET_TYPES } from 'shared/constants/building';

// --- tenant ---

export function formatTenantName(tenant) {
  if (!tenant.first_name && !tenant.last_name) {
    if (!tenant.id) {
      return 'N/A';
    }
    return tenant.id.toString();
  }
  return `${tenant.first_name} ${tenant.last_name}`;
}

export function getTenantSubscriptionQos(tenant) {
  if (!tenant.subscription
    || !tenant.subscription.plan
    || !tenant.subscription.plan.product
    || !tenant.subscription.plan.product.qos
    || !tenant.subscription.plan.product.qos.id
  ) {
    return {};
  }

  return tenant.subscription.plan.product.qos;
}

// --- building/unit ---

export function formatUnitName(name) {
  if (/([0-9])|(^[A-Z ]+$)/.test(name)) {
    if (!name.includes(' ')) {
      return `Unit ${name}`;
    }
  }

  return name;
}

export function formatPhysicalAddress(building, unitName) {
  let addressString = building.address_1 || building.name;
  if (unitName) {
    addressString = `${addressString}, ${unitName}`;
  }
  if (building.city) {
    addressString = `${addressString}, ${building.city}`;
  }
  if (building.state) {
    addressString = `${addressString}, ${building.state}`;
  }
  if (building.zip) {
    addressString = `${addressString}, ${building.zip}`;
  }

  return addressString;
}

export function formatBuildingUpgrade(upgrade) {
  if (upgrade?.plan) {
    return `${upgrade.plan.product.qos.description} for $${priceToString(upgrade.plan.price)}`;
  }
  return `${upgrade.product.qos.description} for $${priceToString(upgrade.price)}`;
}

export function checkGroupDpsk(groupDpsk) {
  const daysOld = Math.trunc((new Date() - new Date(groupDpsk.created_at)) / 1000 / 60 / 60 / 24);
  let status;
  if (daysOld <= 6 * 30) {
    // Less than ~6 months old.
    status = 'NEW';
  } else if (daysOld <= 365) {
    // Between ~6 months and a year.
    status = 'WARNING';
  } else {
    // Anything over a year.
    status = 'EXPIRED';
  }
  return {
    status,
    daysOld,
  };
}

// Returns an object that shows what kind of devices can be created in a unit.
// When disallowReason is set, the building is not configured for any type of device.
// Unit is optional - when included validates that the vlan is set and in the building range.
export function getValidDeviceTypes(building, unit) {
  let disallowReason = '';
  let validVlan = true;

  const method = building.auth_method;
  const service = building.service_type;
  const wirelessAndEthernet = service === INTERNET_TYPES.wirelessAndEthernet;

  const hasDot1x = !!building.dot1x
    && (method === AUTH_METHODS.dot1x || method === AUTH_METHODS.ipv6);
  if (!hasDot1x && !disallowReason) {
    disallowReason = 'building isn\'t configured for dot1x internet';
  }

  const hasNasProfile = hasDot1x && !!building.dot1x.nas_profile_id;
  if (!hasNasProfile && !disallowReason) {
    disallowReason = 'building is missing nas profile';
  }

  const ethernetEnabled = hasNasProfile
    && (wirelessAndEthernet || service === INTERNET_TYPES.ethernet);

  const hasWireless = hasNasProfile
    && (wirelessAndEthernet || service === INTERNET_TYPES.wireless);

  const smartzoneEnabled = hasWireless && !!building.dot1x.smartzone_id;

  const devicesEnabled = ethernetEnabled || smartzoneEnabled;

  if (!disallowReason && !devicesEnabled) {
    if (hasWireless) {
      disallowReason = 'building is missing wireless configuration';
    } else {
      disallowReason = `building service type is "${service}"`;
    }
  }

  if (unit) {
    const { vlan } = unit;
    if (vlan === 0) {
      validVlan = false;
      if (!disallowReason) {
        disallowReason = 'unit vlan isn\'t set';
      }
    } else if (vlan < building.vlan_range_start || vlan > building.vlan_range_end) {
      validVlan = false;
      if (!disallowReason) {
        disallowReason = `unit vlan ${vlan} not in range ${building.vlan_range_start}-${building.vlan_range_end}`;
      }
    }
  }

  return {
    devicesEnabled,
    disallowReason,
    ethernetEnabled,
    hasDot1x,
    hasNasProfile,
    hasWireless,
    smartzoneEnabled,
    validVlan,
  };
}
